import {$window} from '../../../Constants';
import {Settings} from '../Settings';
import Transitions from '../Transitions/Transitions';

/**
 * Slide
 *
 * @return Slide
 */
const Slide = (() => {
	/**
	 * Selector
	 *
	 * @type { Object.<String> }
	 */
	const Selector = {
		IMAGE: '.headerslider-slide-image',
		VIDEO: '.headerslider-slide-video',
		CONTENT: '.headerslider-slide-content',
		CONTENT_INNER: '.headerslider-slide-content-inner',
	};

	/**
	 * ClassName
	 *
	 * @type { Object.<String> }
	 */
	const ClassName = {
		VISIBLE: 'visible'
	};

	/**
	 * HeaderSlider
	 *
	 * @class
	 */
	class Slide {
		/**
		 * @constructor
		 *
		 * @param { jQuery } $image
		 * @param { HeaderSlider } slider
		 */
		constructor($image, slider) {
			this._$image = $image;
			this._$video = this._$image.find(Selector.VIDEO);
			this._slider = slider;
			this._uid = this._$image.data('uid');
			this._$content = this._getContent();
			this._isVideoLoaded = false;
			this._isVideoVisible = false;

			this._transitionImage = new Transitions.FADE(this._$image, Settings.duration);

			if (this._$content) {
				this._transitionContent = new Transitions.FADE(this._$content, Settings.duration);
			}

			if (this._$video.length) {
				if (this._$video.data('video')) {
					this._handleVideo();

					$window.on('resize', () => {
						this._handleVideo();
					});
				} else {
					this._videoPlayer = this._$video.find('video').get(0);

					if (this._videoPlayer) {
						this._isVideoVisible = true;
					}
				}
			}
		}

		// Public

		/**
		 * Shows the image & content
		 *
		 * @param { function } callback
		 * @return void
		 */
		show(callback = null) {
			this._showImage();
			this._showContent(() => {
				if (typeof callback === 'function') {
					callback();
				}
			});
		}

		/**
		 * Hides the image & content
		 *
		 * @param { function } callback
		 * @return void
		 */
		hide(callback = null) {
			this._hideImage(() => {
				if (typeof callback === 'function') {
					callback();
				}
			});

			if (this._$content) {
				this._hideContent();
			}
		}

		// Private

		/**
		 * Returns the content if available
		 *
		 * @return { jQuery | null }
		 */
		_getContent() {
			let $content = this._slider.$contents.find(`${Selector.CONTENT}[data-uid="${this._uid}"]`);

			if ($content.length) {
				return $content;
			}

			return null;
		}

		/**
		 * Shows the image
		 *
		 * @param { function } callback
		 * @return void
		 */
		_showImage(callback = null) {
			this._transitionImage.in(() => {
				if (typeof callback === 'function') {
					callback();
				}
			});

			if (this._isVideoVisible) {
				this._playVideo();
			}
		}

		/**
		 * Hides the image
		 *
		 * @param { function } callback
		 * @return void
		 */
		_hideImage(callback = null) {
			this._transitionImage.out(() => {
				if (typeof callback === 'function') {
					callback();
				}
			});

			if (this._isVideoVisible) {
				this._pauseVideo();
			}
		}

		/**
		 * Shows the content
		 *
		 * @param { function } callback
		 * @return void
		 */
		_showContent(callback = null) {
			if (this._$content) {
				this._slider.$content.removeClass('no-content');
				this._slider.$content.append(this._$content);
				this._slider.setContentHeightAndPosition(this._$content.outerHeight());

				this._transitionContent.in(() => {
					if (this._slider.$activeContent) {
						this._slider.$activeContent.remove();
					}

					this._slider.$activeContent = this._$content;

					if (typeof callback === 'function') {
						callback();
					}
				});
			} else {
				if ($window.width() >= Settings.breakpoint) {
					setTimeout(() => {
						this._slider.$content.addClass('no-content');
						this._slider.$activeContent = null;

						if (typeof callback === 'function') {
							callback();
						}
					}, Settings.duration);
				} else {
					this._slider.$content.addClass('no-content');
					this._slider.$activeContent = null;

					setTimeout(() => {
						if (typeof callback === 'function') {
							callback();
						}
					}, Settings.duration);
				}
			}
		}

		/**
		 * Hides the content
		 *
		 * @param { function } callback
		 * @return void
		 */
		_hideContent(callback = null) {
			this._transitionContent.out(() => {
				if (typeof callback === 'function') {
					callback();
				}
			});
		}

		_handleVideo() {
			if (!this._isVideoLoaded && $window.width() >= 640) {
				this._loadVideo();
			} else if ($window.width() < 640 && this._isVideoVisible) {
				this._hideVideo();
			}

			if ($window.width() >= 640) {
				if (this._isVideoLoaded) {
					this._showVideo();
				} else {
					this._loadVideo();
				}
			} else {
				this._hideVideo();
			}
		}

		_loadVideo() {
			const videoUrl = this._$video.data('video');
			const videoHtml = `<video muted loop playsinline><source src="${videoUrl}" type="video/mp4"></video>`;
			this._$video.append(videoHtml);
			this._videoPlayer = this._$video.find('video').get(0);
			this._isVideoLoaded = true;
			this._isVideoVisible = true;
			this._playVideo();
		}

		_hideVideo() {
			this._$video.css('display', 'none');
			this._pauseVideo();
			this._isVideoVisible = false;
		}

		_showVideo() {
			this._$video.css('display', 'block');
			this._playVideo();
			this._isVideoVisible = true;
		}

		_playVideo() {
			this._videoPlayer.play();
		}

		_pauseVideo() {
			this._videoPlayer.pause();
		}

		// Getters

		/**
		 * Gets the selector constant
		 *
		 * @return { Object } Selector
		 */
		static get Selector() {
			return Selector;
		}
	}

	return Slide;
})();

export default Slide;
